import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import {
  CircleCheck,
  CloudDownload,
  LoaderCircle,
  TriangleAlert,
  WifiOff,
} from 'lucide-react';
import { updateVersionAtom } from '../../store/update/updateState';
import { networkStatusAtom } from '../../store/network/networkState';
import { updateService } from '../../services/updateService';
import { observationDraftService } from '../../services/observationDraftService';
import { getBuildNumber } from '../../utils/getBuildNumber';
import { Button } from '../atoms/Button';

interface UpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Phase = 'idle' | 'saving' | 'updating';

/**
 * The update dialog SAVES rather than warns. Inspection data lives in
 * IndexedDB and survives the update reload - the only thing ever at risk is
 * the in-memory draft buffer (a few seconds of typing awaiting its debounced
 * flush). So: opening the dialog proactively flushes, the status strip shows
 * live truth (saved / saving / could-not-save / offline), Update now
 * re-flushes and verifies before reloading, and the user is only warned in
 * the rare case a flush genuinely failed. The update clears the service-worker
 * caches, and reloading with empty caches and no app host would leave no app
 * at all. So Update now is disabled while the device has no network at all
 * (#10001), and otherwise the update stops unless the app host answers just
 * before the caches are cleared. The online status does not gate it: "online"
 * means the API answers (#11623), and an API-side fault must not stop an
 * installed build from updating to the fix (#10001).
 */
export function UpdateModal({ isOpen, onClose }: UpdateModalProps) {
  const updateVersion = useAtomValue(updateVersionAtom);
  const isOnline = useAtomValue(networkStatusAtom);
  const [phase, setPhase] = useState<Phase>('idle');
  const [hasUnsaved, setHasUnsaved] = useState(false);
  // A genuine flush failure (data at risk) and an unreachable app host are
  // independent conditions that can both be true, so they are tracked
  // separately rather than as mutually exclusive phases: a host-unreachable
  // result must not erase the unsaved-changes warning or revert "Update anyway".
  const [flushFailed, setFlushFailed] = useState(false);
  const [hostUnreachable, setHostUnreachable] = useState(false);
  const [hasNetwork, setHasNetwork] = useState(() => navigator.onLine);

  // Re-read on open: a suspended PWA can miss the online/offline events.
  useEffect(() => {
    if (!isOpen) return;
    const updateHasNetwork = () => setHasNetwork(navigator.onLine);
    updateHasNetwork();
    window.addEventListener('online', updateHasNetwork);
    window.addEventListener('offline', updateHasNetwork);
    return () => {
      window.removeEventListener('online', updateHasNetwork);
      window.removeEventListener('offline', updateHasNetwork);
    };
  }, [isOpen]);

  // While open: kick off a save immediately, then live-poll the draft buffer -
  // the "Saving your latest changes" strip normally resolves to the green
  // all-saved state in front of the user within a second or two.
  useEffect(() => {
    if (!isOpen) {
      setPhase('idle');
      setFlushFailed(false);
      setHostUnreachable(false);
      return;
    }
    if (observationDraftService.hasPendingChanges()) {
      void observationDraftService.flushAll().catch(() => {});
    }
    const check = () =>
      setHasUnsaved(observationDraftService.hasPendingChanges());
    check();
    const timer = setInterval(check, 500);
    return () => clearInterval(timer);
  }, [isOpen]);

  if (!isOpen) return null;

  const startUpdate = async () => {
    setHostUnreachable(false);
    setPhase('updating');
    if (!(await updateService.isAppHostReachable())) {
      setHostUnreachable(true);
      setPhase('idle');
      return;
    }
    await updateService.updateToNewVersion();
  };

  const handleUpdate = async () => {
    setFlushFailed(false);
    setHostUnreachable(false);
    setPhase('saving');
    try {
      await observationDraftService.flushAll();
    } catch {
      // verified below - a failed flush keeps entries dirty
    }
    if (observationDraftService.hasPendingChanges()) {
      setFlushFailed(true);
      setPhase('idle');
      return;
    }
    await startUpdate();
  };

  const formatVersionHash = (versionHash: string | null) => {
    if (!versionHash) return 'Unknown';
    return versionHash.length > 16
      ? `${versionHash.substring(0, 16)}...`
      : versionHash;
  };

  const currentBuild = getBuildNumber();
  const busy = phase === 'saving' || phase === 'updating';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={busy ? undefined : onClose}
      />

      {/* Modal */}
      <div className="relative mx-4 max-w-md rounded-lg bg-white p-6 shadow-lg">
        {/* Same amber CloudDownload mark as the app-bar chip that opened this
            dialog, so the two read as one release-update surface. */}
        <div className="mb-2 flex items-center gap-3">
          <span className="shrink-0 rounded-sm bg-amber-500 p-1.5">
            <CloudDownload className="text-white" size={24} />
          </span>
          <h2 className="text-xl font-bold text-ink">App update available</h2>
        </div>
        <p className="mb-4 text-inkMuted">
          A new version of the application is ready to install.
        </p>

        {updateVersion && (
          <div className="mb-4 rounded-sm bg-line/20 p-3">
            <p className="text-sm text-ink">
              <strong>New version:</strong> {formatVersionHash(updateVersion)}
              {currentBuild && (
                <span className="text-inkMuted">
                  {' '}
                  · replaces build {currentBuild}
                </span>
              )}
            </p>
          </div>
        )}

        {/* Live status strip - exactly one, worst condition wins */}
        {flushFailed ? (
          <div className="mb-6 flex items-start gap-2 border-l-4 border-amber-400 bg-amber-50 p-3">
            <TriangleAlert
              className="mt-0.5 shrink-0 text-amber-600"
              size={18}
            />
            <p className="text-sm text-amber-700">
              <strong>Some recent changes could not be saved.</strong> Updating
              now may lose them. You can go back, check your last entries, and
              try again - or update anyway.
            </p>
          </div>
        ) : hostUnreachable ? (
          <div className="mb-6 flex items-start gap-2 border-l-4 border-amber-400 bg-amber-50 p-3">
            <WifiOff className="mt-0.5 shrink-0 text-amber-600" size={18} />
            <p className="text-sm text-amber-700">
              <strong>The update server cannot be reached.</strong> Nothing was
              changed and your work is safe - try again in a few minutes.
            </p>
          </div>
        ) : !hasNetwork || !isOnline ? (
          <div className="mb-6 flex items-start gap-2 border-l-4 border-amber-400 bg-amber-50 p-3">
            <WifiOff className="mt-0.5 shrink-0 text-amber-600" size={18} />
            <p className="text-sm text-amber-700">
              <strong>You are offline.</strong> Updating needs a connection to
              download the new version
              {hasNetwork
                ? '. If it cannot connect, nothing is changed.'
                : ' - reconnect and try again.'}
            </p>
          </div>
        ) : hasUnsaved || phase === 'saving' ? (
          <div
            className="mb-6 flex items-start gap-2 rounded-sm bg-line/20 p-3"
            aria-live="polite"
          >
            <LoaderCircle
              className="mt-0.5 shrink-0 animate-spin text-inkMuted"
              size={18}
            />
            <p className="text-sm text-inkMuted">
              Saving your latest changes...
            </p>
          </div>
        ) : (
          <div
            className="mb-6 flex items-start gap-2 border-l-4 border-green-500 bg-green-50 p-3"
            aria-live="polite"
          >
            <CircleCheck className="mt-0.5 shrink-0 text-green-600" size={18} />
            <p className="text-sm text-green-800">
              All your inspection work is saved on this device and will survive
              the update.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button
            variant="ghost"
            className="text-inkMuted"
            onClick={onClose}
            disabled={busy}
          >
            Later
          </Button>
          {flushFailed ? (
            <Button
              variant="danger"
              onClick={startUpdate}
              disabled={!hasNetwork || busy}
            >
              Update anyway
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={handleUpdate}
              disabled={!hasNetwork || busy}
            >
              {phase === 'updating'
                ? 'Updating...'
                : phase === 'saving'
                  ? 'Saving...'
                  : 'Update now'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
